import { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Activity, Calendar, MessageSquare, Wallet,
  UserX, ShieldOff, FileWarning, KeyRound, ArrowRight, Timer,
} from "lucide-react";

const Card = ({ title, icon: Icon, accent, children, testid }) => (
  <div
    data-testid={testid}
    className={`bg-white border border-slate-200 ${accent || "border-l-4 border-l-slate-400"} rounded-lg p-5 hover:shadow-md transition-shadow`}
  >
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="h-4 w-4 text-slate-500" />}
      <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</h3>
    </div>
    {children}
  </div>
);

const Row = ({ label, value, tone = "slate", href, testid }) => {
  const toneClasses = {
    slate: "text-slate-700",
    red: "text-red-600 font-semibold",
    amber: "text-amber-700 font-semibold",
    emerald: "text-emerald-700 font-semibold",
    blue: "text-blue-700 font-semibold",
  };
  const content = (
    <>
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`text-sm tabular-nums ${toneClasses[tone]}`}>{value}</span>
    </>
  );
  if (href) {
    return (
      <Link
        to={href}
        data-testid={testid}
        className="flex items-center justify-between py-1.5 px-1 -mx-1 rounded hover:bg-slate-50 group"
      >
        {content}
        <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500 ml-1" />
      </Link>
    );
  }
  return (
    <div className="flex items-center justify-between py-1.5">{content}</div>
  );
};

export function AdminHealthSnapshot() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/dashboard/health-snapshot")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 403 ? "forbidden" : "error"));
  }, []);

  if (err === "forbidden") return null; // hide for non-admin
  if (err) return <div className="text-xs text-red-500">Errore nel caricamento del pannello di controllo.</div>;
  if (!data) {
    return (
      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-5 h-40 animate-pulse" />
        ))}
      </div>
    );
  }

  const { anomalies, kpi, expiring, activity, payments, efficiency } = data;
  const totalAnomalies = (
    anomalies.clients_without_fc + anomalies.policies_without_company +
    anomalies.policies_without_attachments + anomalies.clients_without_portal
  );

  return (
    <div data-testid="admin-health-snapshot" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-violet-600" />
            Pannello di controllo
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Anomalie, KPI e attività operativa dell&apos;agenzia.</p>
        </div>
        {totalAnomalies > 0 && (
          <span className="text-xs px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded-full font-semibold">
            {totalAnomalies} anomali{totalAnomalies === 1 ? "a" : "e"} da risolvere
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* ANOMALIES */}
        <Card title="Anomalie da risolvere" icon={AlertTriangle} accent="border-l-4 border-l-red-500">
          <div className="divide-y divide-slate-100">
            <Row
              label={<span className="inline-flex items-center gap-1.5"><UserX className="h-3.5 w-3.5 text-red-500" /> Clienti senza CF</span>}
              value={anomalies.clients_without_fc}
              tone={anomalies.clients_without_fc ? "red" : "slate"}
              href="/clients?anomaly=no_fc"
              testid="anom-no-fc"
            />
            <Row
              label={<span className="inline-flex items-center gap-1.5"><ShieldOff className="h-3.5 w-3.5 text-red-500" /> Polizze senza compagnia</span>}
              value={anomalies.policies_without_company}
              tone={anomalies.policies_without_company ? "red" : "slate"}
              href="/policies?anomaly=no_company"
              testid="anom-no-company"
            />
            <Row
              label={<span className="inline-flex items-center gap-1.5"><FileWarning className="h-3.5 w-3.5 text-amber-500" /> Polizze senza allegati</span>}
              value={anomalies.policies_without_attachments}
              tone={anomalies.policies_without_attachments ? "amber" : "slate"}
              href="/policies?anomaly=no_attachments"
              testid="anom-no-att"
            />
            <Row
              label={<span className="inline-flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-slate-400" /> Senza portale</span>}
              value={anomalies.clients_without_portal}
              href="/clients?anomaly=no_portal"
              testid="anom-no-portal"
            />
          </div>
        </Card>

        {/* KPI */}
        <Card title="KPI Settimana" icon={Activity} accent="border-l-4 border-l-blue-500">
          <div className="divide-y divide-slate-100">
            <Row label="Preventivi oggi" value={kpi.new_quotes_today} tone="blue" testid="kpi-q-today" />
            <Row label="Preventivi 7gg" value={kpi.new_quotes_week} testid="kpi-q-week" />
            <Row label="Polizze emesse 7gg" value={kpi.policies_issued_week} tone="emerald" testid="kpi-pol-week" />
            <Row label="Accettati 7gg" value={kpi.accepted_quotes_week} testid="kpi-acc-week" />
            <Row label="Conversione" value={`${kpi.conversion_rate}%`} tone="emerald" testid="kpi-conv" />
          </div>
        </Card>

        {/* EXPIRING */}
        <Card title="Scadenze imminenti" icon={Calendar} accent="border-l-4 border-l-orange-500">
          <div className="divide-y divide-slate-100">
            <Row label="Prossimi 7 giorni" value={expiring.in_7d} tone={expiring.in_7d ? "red" : "slate"} href="/policies?filter=expiring_7" testid="exp-7" />
            <Row label="Prossimi 15 giorni" value={expiring.in_15d} tone={expiring.in_15d ? "amber" : "slate"} href="/policies?filter=expiring_15" testid="exp-15" />
            <Row label="Prossimi 30 giorni" value={expiring.in_30d} href="/policies?filter=expiring_30" testid="exp-30" />
            <Row label="Premio a rischio" value={formatCurrency(expiring.premium_at_risk_30d)} tone="amber" testid="exp-premium" />
          </div>
        </Card>

        {/* ACTIVITY */}
        <Card title="Attività cliente" icon={MessageSquare} accent="border-l-4 border-l-violet-500">
          <div className="divide-y divide-slate-100">
            <Row
              label="Portali abilitati"
              value={activity.portals_enabled}
              tone="emerald"
              href="/clients"
              testid="act-portals"
            />
            <Row
              label="Push attive"
              value={activity.push_active}
              tone={activity.push_active ? "blue" : "slate"}
              testid="act-push"
            />
            <Row
              label="Preventivi fermi >7gg"
              value={activity.quotes_stuck_7d}
              tone={activity.quotes_stuck_7d ? "red" : "slate"}
              href="/quotes"
              testid="act-stuck"
            />
          </div>
        </Card>

        {/* PAYMENTS */}
        <Card title="Pagamenti" icon={Wallet} accent="border-l-4 border-l-emerald-500">
          <div className="divide-y divide-slate-100">
            <Row
              label="Incassi oggi"
              value={formatCurrency(payments.received_today_amount)}
              tone="emerald"
              testid="pay-today"
            />
            <Row label="N° pagamenti oggi" value={payments.received_today_count} testid="pay-count-today" />
            <Row
              label="Polizze scadute non pagate"
              value={payments.overdue_count}
              tone={payments.overdue_count ? "red" : "slate"}
              href="/payments"
              testid="pay-overdue"
            />
          </div>
        </Card>

        {/* EFFICIENZA EMISSIONE */}
        <Card title="Efficienza emissione" icon={Timer} accent="border-l-4 border-l-cyan-500" testid="card-efficiency">
          {efficiency && efficiency.sample_size > 0 ? (
            <div className="divide-y divide-slate-100">
              <Row
                label="Tempo medio (30gg)"
                value={`${efficiency.avg_days} ${efficiency.avg_days === 1 ? "giorno" : "giorni"}`}
                tone={efficiency.avg_days <= 2 ? "emerald" : efficiency.avg_days <= 5 ? "amber" : "red"}
                testid="eff-avg"
              />
              <Row label="Campione" value={`${efficiency.sample_size} polizze`} testid="eff-sample" />
              <Row label="Chiusure rapide (≤1gg)" value={efficiency.fast_lt1d} tone="emerald" testid="eff-fast" />
              <Row label="Chiusure lente (>7gg)" value={efficiency.slow_gt7d} tone={efficiency.slow_gt7d ? "amber" : "slate"} testid="eff-slow" />
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-3">
              Nessuna polizza emessa da preventivo negli ultimi 30 giorni. Quando userai il flusso &quot;Origine: da preventivo&quot; durante la creazione della polizza, vedrai qui i tempi medi di chiusura.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

export default AdminHealthSnapshot;
